import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '../auth.service';
import { AuthService as MainAuthService } from '../../../services/auth/auth.service';
import { TokenManagerService } from '../../../services/auth/token-manager.service';
import { Router } from '@angular/router';
import { CoreService } from '../../../services/core/core.service';
import { environment as env } from '../../../../environments/environment';
import { parseJwt } from '../jwt-helpers';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;

  constructor(
    private authService: AuthService,
    private mainAuthService: MainAuthService,
    private tokenManager: TokenManagerService,
    private router: Router,
    private coreService: CoreService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const url = req.url || '';

    // Permitir explícitamente requests de autenticación sin headers (login/refresh/register)
    const isAuthEndpoint = url.includes(env.api.endpoints.auth.endpoint);
    const isAuthActionPath = /\/auth\/(login|refresh|signin|sign-in|register)\b/i.test(url);
    const isAuthActionOnConfiguredEndpoint = isAuthEndpoint && /\b(login|refresh|signin|sign-in|register)\b/i.test(url);
    if (isAuthActionPath || isAuthActionOnConfiguredEndpoint) {
      return next.handle(req);
    }

    // Obtener el token de acceso actual
    const currentUser = this.authService.getCurrentUser();

    // Verificar si el token está expirado ANTES de hacer la petición
    if (currentUser && currentUser.accessToken) {
      try {
        const tokenPayload = parseJwt(currentUser.accessToken);
        const now = Math.floor(Date.now() / 1000);

        if (tokenPayload.exp && tokenPayload.exp < now) {
          console.warn('⏰ AuthInterceptor: Token expirado detectado antes de la petición');
          // Token expirado, intentar refrescar inmediatamente
          return this.handle401Error(req, next);
        }
      } catch (error) {
        console.error('❌ AuthInterceptor: Error al parsear token:', error);
        // Si no se puede parsear el token, es inválido
        this.redirectToLogin('invalid_token');
        return throwError('Invalid token');
      }
    }

    if (currentUser && currentUser.accessToken) {
      const orgId = this.coreService.resolveCurrentOrganizationId();
      const method = (req.method || 'GET').toUpperCase();
      const action = method === 'GET' || method === 'HEAD' ? 'read' : method === 'DELETE' ? 'delete' : 'write';

      // Inferir servicio según URL y ruta
      let service = 'platform';

      // Detectar por prefijos de ruta (más específico primero)
      if (url.includes('/lelfunko/')) {
        service = 'lelfunko';
      } else if (url.includes('/airnode/')) {
        service = 'airnode';
      }
      // Detectar platform por endpoints core/auth
      else if (url.includes(env.api.endpoints.core.endpoint) || url.includes(env.api.endpoints.auth.endpoint)) {
        service = 'platform';
      }

      const headers: Record<string, string> = {
        Authorization: `Bearer ${currentUser.accessToken}`,
        'X-Rbac-Service': service,
        'X-Rbac-Action': action,
      };
      if (orgId) headers['X-Tenant-Id'] = orgId;

      // Opcional: redundar sub desde idToken
      try {
        if (currentUser.idToken) {
          const sub = parseJwt(currentUser.idToken).sub;
          if (sub) headers['X-User-Sub'] = sub;
        }
      } catch {}

      const authReq = req.clone({ setHeaders: headers });

      return next.handle(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401) {
            // Token expirado o inválido
            return this.handle401Error(req, next);
          } else if (error.status === 403) {
            // Sin permisos
            this.handle403Error();
          }
          return throwError(error);
        })
      );
    }

    return next.handle(req);
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Intentar refrescar el token si no se está refrescando ya
    if (!this.isRefreshing) {
      this.isRefreshing = true;

      const refreshToken = this.mainAuthService.getRefreshToken();

      if (refreshToken) {
        // Intentar refrescar el token
        return from(this.mainAuthService.refreshTokens()).pipe(
          switchMap(() => {
            this.isRefreshing = false;
            // Reintentar la petición original con el nuevo token
            const newUser = this.authService.getCurrentUser();
            if (newUser && newUser.accessToken) {
              const clonedReq = this.cloneRequestWithToken(req, newUser.accessToken);
              return next.handle(clonedReq);
            }
            return throwError('No access token after refresh');
          }),
          catchError((error) => {
            this.isRefreshing = false;
            // Si falla el refresh, cerrar sesión
            console.error('❌ AuthInterceptor: Error al refrescar token, cerrando sesión.');
            this.redirectToLogin('refresh_failed');
            return throwError(error);
          })
        );
      }
    }

    // Si no hay refresh token o ya se está refrescando, cerrar sesión
    this.redirectToLogin('token_expired');
    return throwError('Authentication failed');
  }

  private cloneRequestWithToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    const orgId = this.coreService.resolveCurrentOrganizationId();
    const method = (req.method || 'GET').toUpperCase();
    const action = method === 'GET' || method === 'HEAD' ? 'read' : method === 'DELETE' ? 'delete' : 'write';
    const url = req.url || '';

    let service = 'platform';
    if (url.includes('/lelfunko/')) service = 'lelfunko';
    else if (url.includes('/airnode/')) service = 'airnode';
    else if (url.includes(env.api.endpoints.core.endpoint) || url.includes(env.api.endpoints.auth.endpoint)) service = 'platform';

    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      'X-Rbac-Service': service,
      'X-Rbac-Action': action,
    };
    if (orgId) headers['X-Tenant-Id'] = orgId;

    return req.clone({ setHeaders: headers });
  }

  private handle403Error(): void {
    // Mostrar mensaje de error o redirigir a página de acceso denegado
    console.warn('⛔ Access denied: Insufficient permissions');
  }

  private redirectToLogin(reason: string = 'token_expired'): void {
    console.warn(`🔒 AuthInterceptor: Redirigiendo a login. Razón: ${reason}`);
    this.tokenManager.stopTokenRefreshMonitoring();
    this.mainAuthService.clearSession();
    this.authService.logout();
    this.router.navigate(['/login'], {
      queryParams: {
        returnUrl: this.router.url,
        reason: reason,
      },
    });
  }
}
